import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import type { Environment } from '../components/EnvironmentSwitcher';

interface OrganizationSelection {
  orgId: number | null;
  orgName: string | null;
  timestamp?: number;
}

interface OrganizationContextType {
  orgId: number | null;
  orgName: string | null;
  setSelection: (orgId: number | null, orgName?: string | null) => void;
  clearSelection: () => void;
  isLoading: boolean;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

interface OrganizationProviderProps {
  children: ReactNode;
}

const STORAGE_KEY_PREFIX = 'selectedOrg_';

export function OrganizationProvider({ children }: OrganizationProviderProps) {
  const [orgId, setOrgId] = useState<number | null>(null);
  const [orgName, setOrgName] = useState<string | null>(null);
  const [currentEnvironment, setCurrentEnvironment] = useState<Environment>('dev');
  const [isLoading, setIsLoading] = useState(true);

  // Load selection from localStorage for a specific environment
  const loadSelectionFromStorage = useCallback((env: Environment) => {
    try {
      const storageKey = `${STORAGE_KEY_PREFIX}${env}`;
      const stored = localStorage.getItem(storageKey);
      
      if (stored) {
        const parsed: OrganizationSelection = JSON.parse(stored);
        // Validate the stored data
        if (parsed.orgId !== null && parsed.orgId !== undefined) {
          setOrgId(parsed.orgId);
          setOrgName(parsed.orgName || null);
        } else {
          setOrgId(null);
          setOrgName(null);
        }
      } else {
        setOrgId(null);
        setOrgName(null);
      }
    } catch (error) {
      console.warn('Failed to load organization selection from storage:', error);
      setOrgId(null);
      setOrgName(null);
    }
  }, []);

  // Save selection to localStorage for current environment
  const saveSelectionToStorage = useCallback((env: Environment, selection: OrganizationSelection) => {
    try {
      const storageKey = `${STORAGE_KEY_PREFIX}${env}`;
      localStorage.setItem(storageKey, JSON.stringify(selection));
    } catch (error) {
      console.warn('Failed to save organization selection to storage:', error);
    }
  }, []);

  // Handle environment change (called when environment is detected or changes)
  const handleEnvironmentChange = useCallback((newEnv: Environment) => {
    setCurrentEnvironment((prevEnv) => {
      // If environment changed, clear selection to prevent cross-environment confusion
      if (newEnv !== prevEnv && prevEnv !== 'dev') {
        setOrgId(null);
        setOrgName(null);
        // Clear localStorage for old environment
        try {
          localStorage.removeItem(`${STORAGE_KEY_PREFIX}${prevEnv}`);
        } catch (error) {
          console.warn('Failed to clear old environment selection:', error);
        }
      }
      
      // Load selection for new environment
      loadSelectionFromStorage(newEnv);
      
      return newEnv;
    });
  }, [loadSelectionFromStorage]);

  // Set organization selection
  const setSelection = useCallback((newOrgId: number | null, newOrgName?: string | null) => {
    setOrgId(newOrgId);
    setOrgName(newOrgName || null);
    
    if (newOrgId !== null) {
      const selection: OrganizationSelection = {
        orgId: newOrgId,
        orgName: newOrgName || null,
        timestamp: Date.now(),
      };
      saveSelectionToStorage(currentEnvironment, selection);
    } else {
      // Clear selection
      try {
        const storageKey = `${STORAGE_KEY_PREFIX}${currentEnvironment}`;
        localStorage.removeItem(storageKey);
      } catch (error) {
        console.warn('Failed to clear organization selection from storage:', error);
      }
    }
  }, [currentEnvironment, saveSelectionToStorage]);

  // Clear organization selection
  const clearSelection = useCallback(() => {
    setSelection(null, null);
  }, [setSelection]);

  // Listen for environment changes from EnvironmentSwitcher
  useEffect(() => {
    const handleEnvironmentUpdate = (event: CustomEvent<Environment>) => {
      handleEnvironmentChange(event.detail);
      setIsLoading(false);
    };

    // Listen for custom event when environment is detected/changed
    window.addEventListener('environmentChanged' as any, handleEnvironmentUpdate as EventListener);
    
    // Also check if environment is already known (from localStorage or default)
    // Try to get from a shared location or default to 'dev'
    const storedEnv = localStorage.getItem('currentEnvironment') as Environment | null;
    const initialEnv = storedEnv || 'dev';
    handleEnvironmentChange(initialEnv);
    setIsLoading(false);

    return () => {
      window.removeEventListener('environmentChanged' as any, handleEnvironmentUpdate as EventListener);
    };
  }, [handleEnvironmentChange]);

  const value: OrganizationContextType = {
    orgId,
    orgName,
    setSelection,
    clearSelection,
    isLoading,
  };

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
}

export function useOrganizationSelection(): OrganizationContextType {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganizationSelection must be used within an OrganizationProvider');
  }
  return context;
}

